import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { MapPin, Route, Camera, CheckCircle2 } from "lucide-react";
import { localBusinessSchema, faqSchema, breadcrumbSchema } from "@/lib/localBusiness";
import { CheckAvailabilityCta } from "@/components/CheckAvailabilityCta";

const PATH = "/atlanta-studio-space-rental";
const URL = `https://bookmystudiospace.com${PATH}`;

const faqs = [
  {
    q: "Do you have a studio space in Atlanta?",
    a: "Our studio is in Columbus, GA — about 100 miles and roughly a 1 hour 45 minute drive southwest of downtown Atlanta via I-85 and I-185. Atlanta-based photographers, brands, and production teams book us for shoots in the Columbus / Fort Moore / Phenix City market and for day-rate work that doesn't need to sit in metro traffic.",
  },
  {
    q: "Why book here instead of a studio inside the perimeter?",
    a: "Two reasons: cost and calendar. Hourly rates start at $45 for Studio A and $70 for Studio B with the cyc wall, and because we're outside the metro crunch you can usually get the exact date and hours you want — including same-day blocks Monday through Saturday.",
  },
  {
    q: "What does a photo studio rental include?",
    a: "Strobe and continuous lighting with modifiers, stands, seamless paper backdrops, a full cyclorama wall in Studio B, a beauty station and changing area, and client lounge seating. You bring bodies, lenses, and wardrobe; the lighting and surfaces are already here.",
  },
  {
    q: "Is there parking and easy load-in for a production van?",
    a: "Yes. Free onsite parking is directly outside the suite at 2300 Whittlesey Rd Ste E with ground-level access — no freight elevator, no loading dock queue, no metered street parking.",
  },
  {
    q: "Can we book a full day for a crew coming down from Atlanta?",
    a: "Book consecutive hourly blocks online for a full production day, or contact us for multi-day and recurring content-day scheduling. Confirmation emails include directions and the studio rules so your crew can roll straight in.",
  },
  {
    q: "Is the space suitable for video and podcast production?",
    a: "Yes. The rooms are sound-treated for interview, podcast, and talking-head recording, and the cyc wall gives you a seamless infinity background for product video, music video, and brand content.",
  },
];

const proof = [
  "Operating as a dedicated rental studio in Georgia since 2017",
  "Two distinct rooms: Studio A for portraits and product, Studio B with a full cyclorama wall",
  "Professional lighting and grip already rigged — no gear rental invoice on top of your booking",
  "Real-time online calendar: see open hours and confirm instantly, no quote-and-wait",
  "Portfolio of portrait, editorial, maternity, and brand work shot in these rooms",
];

const AtlantaStudioSpace = () => (
  <div className="pt-20">
    <Helmet>
      <title>Studio Space Atlanta Creators Rent | Photo Studio Rental Near Me</title>
      <meta
        name="description"
        content="Atlanta photographers and brands: rent studio space under two hours from the city. Cyc wall, lighting, free parking, from $45/hr. Book your photo studio rental online."
      />
      <link rel="canonical" href={URL} />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
      <meta property="og:title" content="Studio Space Atlanta Creators Rent | Photo Studio Rental Near Me" />
      <meta property="og:description" content="Photo and video studio space for Atlanta-area creators — cyc wall, pro lighting, free parking, hourly rates from $45." />
      <meta property="og:url" content={URL} />
      <meta property="og:type" content="website" />
      <script type="application/ld+json">{JSON.stringify(localBusinessSchema())}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema(faqs))}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Atlanta Studio Space Rental", path: PATH },
      ]))}</script>
    </Helmet>

    <section className="py-20 border-b border-border">
      <div className="container mx-auto px-4 max-w-4xl text-center">
        <div className="inline-flex items-center gap-2 text-primary font-mono text-xs tracking-widest uppercase mb-4">
          <Route className="w-4 h-4" /> 1 hr 45 min from downtown Atlanta via I-85 → I-185
        </div>
        <h1 className="text-4xl md:text-6xl font-display tracking-widest mb-6">
          STUDIO SPACE FOR ATLANTA CREATORS
        </h1>
        <p className="text-muted-foreground font-body text-lg max-w-2xl mx-auto mb-8">
          When every studio inside the perimeter is booked, priced for a metro market, or three
          flights of stairs from your gear — MyStudio in Columbus, GA gives Atlanta photographers,
          videographers, and brands a full cyc wall, rigged lighting, and free door-front parking
          from $45/hour.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <CheckAvailabilityCta city="atlanta" campaign="atlanta_studio_space" placement="hero">
            See Open Dates
          </CheckAvailabilityCta>
          <Link to="/studios">
            <Button variant="outline" className="border-border font-body px-8">Tour Studio A &amp; B</Button>
          </Link>
        </div>
      </div>
    </section>

    <section className="py-16">
      <div className="container mx-auto px-4 max-w-4xl">
        <h2 className="text-2xl md:text-3xl font-display tracking-widest text-center mb-10">
          WHY CREWS MAKE THE DRIVE
        </h2>
        <div className="space-y-3">
          {proof.map((p) => (
            <div key={p} className="bg-panel border border-border p-5 flex gap-3">
              <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <p className="text-sm font-body text-foreground/85">{p}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <Link to="/gallery" className="text-primary font-mono text-xs tracking-widest hover:underline">
            SEE WORK SHOT IN THESE ROOMS →
          </Link>
        </div>
      </div>
    </section>

    <section className="py-16 border-t border-border">
      <div className="container mx-auto px-4 max-w-4xl">
        <h2 className="text-2xl md:text-3xl font-display tracking-widest text-center mb-10">
          WHAT YOU CAN SHOOT
        </h2>
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { title: "PORTRAIT & EDITORIAL", body: "Headshots, fashion, maternity, and campaign portraits with modifiers already on stands." },
            { title: "PRODUCT & BRAND", body: "Seamless cyc wall and clean sweeps for e-commerce, packaging, and brand content days." },
            { title: "VIDEO & PODCAST", body: "Sound-treated rooms for interviews, music video, and talking-head production." },
          ].map((c) => (
            <div key={c.title} className="bg-panel border border-border p-6 text-center">
              <Camera className="w-6 h-6 text-primary mx-auto mb-3" />
              <h3 className="font-display tracking-widest text-sm mb-2">{c.title}</h3>
              <p className="text-xs text-muted-foreground font-body">{c.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 border-t border-border">
      <div className="container mx-auto px-4 max-w-3xl">
        <h2 className="text-2xl md:text-3xl font-display tracking-widest text-center mb-10">
          ATLANTA-TO-COLUMBUS FAQ
        </h2>
        <div className="space-y-4">
          {faqs.map((f) => (
            <div key={f.q} className="bg-panel border border-border p-6">
              <h3 className="font-display tracking-widest text-primary text-base mb-2">{f.q}</h3>
              <p className="text-sm font-body text-foreground/80">{f.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 border-t border-border">
      <div className="container mx-auto px-4 max-w-3xl text-center">
        <div className="inline-flex items-center gap-2 text-primary font-mono text-xs tracking-widest uppercase mb-4">
          <MapPin className="w-4 h-4" /> 2300 Whittlesey Rd Ste E · Columbus, GA 31909
        </div>
        <h2 className="text-2xl font-display tracking-widest mb-6">BOOK YOUR SHOOT DAY</h2>
        <CheckAvailabilityCta
          city="atlanta"
          campaign="atlanta_studio_space"
          placement="footer"
          className="bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold px-10"
        >
          Check Availability
        </CheckAvailabilityCta>
        <p className="text-xs font-mono tracking-widest text-muted-foreground mt-8">
          Local to Columbus? <Link to="/photo-studio-near-me-columbus-ga" className="text-primary hover:underline">Photo studio near me — Columbus, GA</Link>
          {" · "}
          <Link to="/columbus-ga-photo-studio-rental" className="text-primary hover:underline">Columbus studio rental</Link>
          {" · "}
          <Link to="/rates" className="text-primary hover:underline">Rates</Link>
        </p>
      </div>
    </section>
  </div>
);

export default AtlantaStudioSpace;
